import {
  Component,
  Output,
  EventEmitter,
  Input,
  ViewChild,
  ElementRef,
  OnInit,
  signal,
  computed,
} from "@angular/core";
import { TaskDataService } from "../../../shared/services/task-data.service";
import { ITaskRequest, TaskRequest } from "../../../modules/tasks/model/types";
import notify from "devextreme/ui/notify";
import validationEngine from "devextreme/ui/validation_engine";
import {
  getNotificationTimeout,
  sendNotificationMessage,
} from "../../../types/resource";
import { LoggingService } from "../../../framework/logging/logging.service";
import { SeverityLevel } from "../../../framework/logging/severity-level.model";
import { firstValueFrom, lastValueFrom } from "rxjs";
import { map } from "rxjs/operators";
import { MessageService } from "../../../shared/components/framework/services/message.service";
import { FormUtils } from "../../../shared/components/framework/services/form-builder.service";
import { LoanDataBase } from "../baseclass/loandata-base";
import {
  IFormSummary,
  IWidgetConfig,
  IFileUploadConfig,
  ILoanAmountGridConfig,
  IDisplayLabelConfig,
} from "../../../framework/models/form-template";
import { DynamicFormBuilderService } from "../../../framework/services/dynamic-form-builder.service";
import { IDynamicFormConfig } from "../../../framework/models/dynamic-form";
import { FeatureFlagsService } from "../../../shared/services/feature-flags.service";

/** Lightweight request-type option derived from IFormSummary. */
interface IDynamicRequestTypeOption {
  /** formType — used as the key to fetch the full template. */
  id: string;
  /** Display name shown in the Request Type drop-down. */
  name: string;
  type: "dynamic";
  referenceType?: string;
}

const AUDIT_CONFIRM_FORM_TYPE = "AUDIT_CONFIRM";
const AVIVA_GATED_LINKED_TEMPLATE_PREFIX = "LOAN EVENT - AMENDMENT OF SECURITY";

/**
 * ServiceRequestDynamicComponent
 *
 * A self-contained form component that only handles DB-driven dynamic forms.
 * It has no knowledge of the legacy switch-case form configs from
 * ServiceRequestComponent, making it easier to extend and test independently.
 *
 * Usage (inside task-detail or new-task):
 *   <app-service-request-dynamic
 *     [taskTemplateName]="'Servicing Request'"
 *     [task]="existingTask"
 *     (onAddNewTaskEventEmitter)="onTaskCreated($event)">
 *   </app-service-request-dynamic>
 */
@Component({
  standalone: false,
  selector: "app-service-request-dynamic",
  templateUrl: "./service-request-dynamic.component.html",
  styleUrl: "./service-request-dynamic.component.scss",
})
export class ServiceRequestDynamicComponent
  extends LoanDataBase
  implements OnInit
{
  @ViewChild("formElement") formElement!: ElementRef;

  @Input() taskTemplateName: string;
  @Input() task: ITaskRequest;
  /** When set, only form summaries whose linkedTemplateName matches this value are shown. */
  @Input() linkedTemplateFilter?: string;

  @Output() onAddNewTaskEventEmitter: EventEmitter<any> = new EventEmitter();

  // ── Request types ─────────────────────────────────────────────────────────
  /** Populated from the API on init — only dynamic form templates. */
  requestTypes: IDynamicRequestTypeOption[] = [];

  // ── Form state ────────────────────────────────────────────────────────────
  requestData: any = {};
  files: File[] = [];
  isLoading: boolean = false;
  isEditable: boolean = false;
  today: Date = new Date();

  /** Current DevExtreme form instance — set by (onInitialized) handler. */
  currentFormInstance: any = null;

  /**
   * Whether inactive loans are currently shown.
   * Passed down to app-loan-amount-grid so it can filter lookups.
   */
  override showInactiveLoans: boolean = false;

  // ── Dynamic form signals ──────────────────────────────────────────────────
  readonly dynamicFormConfig = signal<IDynamicFormConfig | null>(null);

  /** Stable array reference for [items] binding — avoids re-creating on every CD cycle. */
  readonly dynamicFormItems = computed<any[]>(
    () => (this.dynamicFormConfig()?.items ?? []) as any[]
  );

  constructor(
    taskDataService: TaskDataService,
    private readonly loggingService: LoggingService,
    private readonly messageService: MessageService,
    private readonly dynamicFormBuilderService: DynamicFormBuilderService,
    private readonly featureFlagsService: FeatureFlagsService
  ) {
    super(taskDataService);
    this.onRequestTypeSelectionChanged =
      this.onRequestTypeSelectionChanged.bind(this);
  }

  // ── File upload ───────────────────────────────────────────────────────────

  onFileUpload(file: File): void {
    this.files.push(file);
    this.updateFileUploadProxy();
  }

  onFileRemove(file: File): void {
    if (file != null && file != undefined) {
      this.files = this.files.filter((f) => f !== file);
    }
    this.updateFileUploadProxy();
  }

  private updateFileUploadProxy(): void {
    const proxyEditor = this.currentFormInstance?.getEditor("fileUploadProxy");
    if (proxyEditor) {
      proxyEditor.option("value", this.files.length.toString());
    }
  }

  // ── Loan amount grid ──────────────────────────────────────────────────────

  onLoanAmountValueChange(fieldKey: string, rows: any[]): void {
    this.requestData[fieldKey] = rows;
    const proxyEditor =
      this.currentFormInstance?.getEditor("loanAmountsProxy");
    if (proxyEditor) {
      proxyEditor.option("value", rows.length.toString());
    }
    if (fieldKey === 'loanAmounts') {
      this.requestData['bookName'] = rows[0]?.bookName ?? '';
    }
  }

  // ── Form lifecycle ────────────────────────────────────────────────────────

  onFormInitialized(e: any): void {
    this.currentFormInstance = e.component;
  }

  // ── Dynamic widget config helpers ─────────────────────────────────────────

  /**
   * Returns the metadata config for a named custom widget item from the current
   * dynamic form config.  We look this up from the signal rather than relying
   * on DevExtreme passing custom properties through the *dxTemplate context.
   */
  getWidgetConfig(name: string): IWidgetConfig | undefined {
    return (this.dynamicFormConfig()?.items as any[])?.find(
      (i: any) => i.name === name
    )?.metadata;
  }

  getFileUploadConfig(name: string): IFileUploadConfig | undefined {
    return this.getWidgetConfig(name) as IFileUploadConfig | undefined;
  }

  getLoanAmountGridConfig(name: string): ILoanAmountGridConfig | undefined {
    return this.getWidgetConfig(name) as ILoanAmountGridConfig | undefined;
  }

  getDisplayLabelConfig(name: string): IDisplayLabelConfig | undefined {
    return this.getWidgetConfig(name) as IDisplayLabelConfig | undefined;
  }

  // ── Request type selection ────────────────────────────────────────────────

  async onRequestTypeSelectionChanged(e: any): Promise<void> {
    const selectedItem = e.selectedItem as IDynamicRequestTypeOption;
    if (!selectedItem?.id) return;

    try {
      this.isLoading = true;

      const template = await firstValueFrom(
        this.taskDataService.getFormTemplate(selectedItem.id)
      );

      const config = this.dynamicFormBuilderService.buildDynamicForm(template);

      this.dynamicFormBuilderService.injectFileUploadProxyCallbacks(
        config,
        () => this.files
      );
      this.dynamicFormBuilderService.injectLoanAmountProxyCallbacks(
        config,
        (fieldKey) => this.requestData[fieldKey] ?? []
      );
      this.dynamicFormBuilderService.injectBehaviorCallbacks(
        config,
        this.requestData,
        () => this.currentFormInstance
      );
      this.dynamicFormBuilderService.injectValidationCallbacks(
        config,
        this.requestData
      );

      await firstValueFrom(
        this.dynamicFormBuilderService.resolveDataSources(config)
      );

      // Ensure array fields for loan-amount grids are initialised before the
      // form renders so the grid never receives undefined as its [value] input.
      config.items.forEach((item: any) => {
        if (
          item.template === "loanAmountGridTemplate" &&
          !Array.isArray(this.requestData[item.name])
        ) {
          this.requestData[item.name] = [];
        }
      });

      this.dynamicFormConfig.set(config);
    } finally {
      this.isLoading = false;
    }
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  async onSubmit(e: any): Promise<void> {
    e.preventDefault();

    const validationResult = validationEngine.validateGroup("formGroup");
    if (!validationResult.isValid) return;

    try {
      const requestType = this.requestTypes.find(
        (t) => t.name === this.requestData.requestType
      );
      if (!requestType) return;

      const request: ITaskRequest = new TaskRequest();
      request.linkedTemplateName = requestType.referenceType;
      request.name = requestType.name;

      if (
        this.requestData.loanAmounts != null &&
        this.requestData.loanAmounts.length > 0
      ) {
        request.referenceName = `${this.requestData.loanAmounts[0].loanNumber}`;
      } else {
        request.referenceName =
          this.requestData.loanNumber || this.requestData.dealNumber || "";
        this.requestData.loanNumber = this.requestData.loanNumber || "";
      }

      request.referenceType = requestType.referenceType;

      // Work on a copy so we don't mutate requestData before the form is reset.
      let submissionData = { ...this.requestData };

      const isAuditConfirm =
        requestType.name?.toUpperCase() === "AUDIT CONFIRM" ||
        requestType.id?.toUpperCase() === AUDIT_CONFIRM_FORM_TYPE;

      // Convert selectedDealLoanNumbers display string back to an array for storage.
      if (
        submissionData.selectedDealLoanNumbers &&
        typeof submissionData.selectedDealLoanNumbers === "string"
      ) {
        submissionData.selectedDealLoanNumbers = submissionData.selectedDealLoanNumbers
          .split(",")
          .map((ln: string) => ln.trim())
          .filter((ln: string) => ln.length > 0);
      }

      if (isAuditConfirm && Array.isArray(submissionData.loanAmounts)) {
        submissionData.loanNumbers = submissionData.loanAmounts.map(
          (row: any) => row.loanNumber
        );
        delete submissionData.loanAmounts;
      }

      // Roll up per-row amounts into a top-level total.
      if (!isAuditConfirm && submissionData.loanAmounts) {
        submissionData.amount = submissionData.loanAmounts.reduce(
          (sum: number, la: any) => sum + Number(la.amount || 0),
          0
        );
      }

      submissionData = this.appendDealNameToSubmission(submissionData);

      const formData = new FormData();
      formData.append("linkedTemplateName", request.linkedTemplateName ?? "");
      formData.append("locations", request.locations ?? "");
      formData.append("name", request.name);
      formData.append("referenceName", request.referenceName);
      formData.append(
        "requestJsonData",
        this.removeProxyData(JSON.stringify(submissionData))
      );
      formData.append("referenceType", request.referenceType ?? "");

      if (this.isEditable) formData.append("parentId", this.task.parentId);

      this.files.forEach((f) => {
        formData.append("files", f, f.name);
      });

      this.isLoading = true;

      const response = await lastValueFrom(
        this.taskDataService.insertTaskAsFormData(formData)
      );

      sendNotificationMessage(
        `${response[1].data[0].task.taskId} task created`,
        this.messageService
      );

      this.isLoading = false;
      this.formElement.nativeElement.reset();
      this.formElement.nativeElement.submit();
    } catch (err: any) {
      this.isLoading = false;
      this.loggingService.logError(err, SeverityLevel.Error);
      notify(err.message, "error", getNotificationTimeout());
    }
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  override async ngOnInit(): Promise<void> {
    try {
      this.isLoading = true;

      // Load only dynamic form templates for this task template name.
      const summaries = await firstValueFrom(
        this.taskDataService.getFormTemplates()
      );
      const selectedTemplateType = this.taskTemplateName?.trim().toUpperCase();
      let filteredSummaries = summaries;

      if (this.linkedTemplateFilter) {
        filteredSummaries = summaries.filter(
          (s: IFormSummary) =>
            s.linkedTemplateName?.trim().toUpperCase() ===
            this.linkedTemplateFilter!.trim().toUpperCase()
        );
      } else {
        const requestTypeData = await lastValueFrom(
          this.taskDataService.getTaskTemplatesByType(this.taskTemplateName)
        );
        const workflowTemplates = requestTypeData?.[1] ?? [];
        const linkedTemplateNames = new Set(
          workflowTemplates
            .map((t: any) => t?.name?.trim().toUpperCase())
            .filter(Boolean)
        );

        filteredSummaries = summaries.filter((s: IFormSummary) =>
          linkedTemplateNames.has(s.linkedTemplateName?.trim().toUpperCase())
        );
      }

      this.requestTypes = filteredSummaries.map((s: IFormSummary) => ({
        id: s.formType,
        name: s.displayName,
        type: "dynamic" as const,
        referenceType: s.linkedTemplateName,
      }));

      // Hide region-gated request types (e.g. "Loan Event - Amendment of
      // Security") when the corresponding feature flag is off. The server
      // will also reject direct POSTs, but filtering here avoids an
      // unusable option appearing in the dropdown.
      if (!this.linkedTemplateFilter && !this.featureFlagsService.isEu()) {
        this.requestTypes = this.requestTypes.filter(
          (t) =>
            !(t.referenceType ?? "")
              .trim()
              .toUpperCase()
              .startsWith(AVIVA_GATED_LINKED_TEMPLATE_PREFIX)
        );
      }

      // Load loan + deal data (base class).
      await super.ngOnInit();

      this.isEditable = this.task != null;

      if (this.isEditable) {
        this.requestData = FormUtils.mapFromTask<any>(this.task, function () {
          return {};
        } as any);

        if (
          this.requestData.requestType?.toUpperCase() === "AUDIT CONFIRM" &&
          this.requestData.loanNumbers &&
          !this.requestData.loanAmounts
        ) {
          this.requestData.loanAmounts = this.requestData.loanNumbers.map(
            (loanNumber: string) => ({
              key: this.generateUniqueId(),
              loanNumber,
              name: loanNumber,
              amount: 0,
              effectiveDate: new Date(),
            })
          );
        }

        // Normalise loanAmounts rows so the grid has a stable key and all
        // expected fields populated from the loaded loan data.
        if (
          this.requestData.loanAmounts &&
          this.requestData.loanAmounts.length > 0
        ) {
          this.requestData.loanAmounts = this.requestData.loanAmounts.map(
            (amount: any) => ({
              key: amount.key || this.generateUniqueId(),
              amount: amount.amount,
              name: amount.loanNumber,
              loanNumber: amount.loanNumber,
              dealName:
                this.loanData?.find((d) => d.name === amount.loanNumber)
                  ?.dealName ?? null,
            })
          );
        }

        // Trigger form-config build for the saved request type.
        if (this.requestData.requestType) {
          const requestTypeObj = this.requestTypes.find(
            (rt) => rt.name === this.requestData.requestType
          );
          if (requestTypeObj) {
            await this.onRequestTypeSelectionChanged({
              selectedItem: requestTypeObj,
            });
          }
        }
      }

      this.isLoading = false;
    } catch (err: any) {
      this.loggingService.logError(err, SeverityLevel.Error);
      this.isLoading = false;
      notify(err.message, "error", getNotificationTimeout());
    }
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private removeProxyData(data: string): string {
    const json = JSON.parse(data);
    delete json["fileUploadProxy"];
    delete json["loanAmountsProxy"];
    return JSON.stringify(json);
  }

  private generateUniqueId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

}